import { Money } from '../ui/Money';
import React, { useState } from 'react';
import axios from 'axios';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Package, Plus, Trash2, ChevronDown, ChevronUp, Edit2, Check, X, FileDown, Settings2, Loader2 } from 'lucide-react';
import { Badge } from '../ui/badge';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { toast } from '../../hooks/use-toast';
import DeleteConfirmDialog from '../DeleteConfirmDialog';

const API = process.env.REACT_APP_BACKEND_URL;
const CONDITION_OPTIONS = ['New', 'Excellent', 'Good', 'Fair', 'Poor'];
const STATUS_OPTIONS = ['Assigned', 'Returned', 'Lost', 'Damaged'];

const AssetsSection = ({ formData, setFormData, employeeId }) => {
  const [showAddForm, setShowAddForm] = useState(false);
  const [expandedAssets, setExpandedAssets] = useState({});
  const [editingIndex, setEditingIndex] = useState(null);
  const [editingAsset, setEditingAsset] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [assetToDelete, setAssetToDelete] = useState(null);
  const [downloading, setDownloading] = useState(null);
  const [showDiscSettings, setShowDiscSettings] = useState(false);
  const [disc, setDisc] = useState(null);
  const [savingDisc, setSavingDisc] = useState(false);
  const [newAsset, setNewAsset] = useState({
    product_name: '',
    serial_number: '',
    condition_given: 'New',
    condition_returned: '',
    cost: '',
    date_given: new Date().toISOString().split('T')[0],
    date_returned: '',
    notes: '',
    status: 'Assigned'
  });

  const assets = formData.assets || [];

  const addAsset = () => {
    if (!newAsset.product_name) {
      alert('Please enter the product name');
      return;
    }

    const assetWithId = {
      ...newAsset,
      id: Date.now().toString(),
      cost: parseFloat(newAsset.cost) || 0
    };

    setFormData(prev => ({
      ...prev,
      assets: [...(prev.assets || []), assetWithId]
    }));

    setNewAsset({
      product_name: '',
      serial_number: '',
      condition_given: 'New',
      condition_returned: '',
      cost: '',
      date_given: new Date().toISOString().split('T')[0],
      date_returned: '',
      notes: '',
      status: 'Assigned'
    });
    setShowAddForm(false);
  };

  const removeAsset = (index) => {
    const asset = assets[index];
    setAssetToDelete({ index, name: asset.product_name, serial: asset.serial_number });
    setDeleteConfirmOpen(true);
  };

  const confirmRemoveAsset = () => {
    if (assetToDelete === null) return;
    
    setFormData(prev => ({
      ...prev,
      assets: prev.assets.filter((_, i) => i !== assetToDelete.index)
    }));
    setAssetToDelete(null);
  };

  const startEditing = (index, asset) => {
    setEditingIndex(index);
    setEditingAsset({ ...asset });
  };

  const cancelEditing = () => {
    setEditingIndex(null);
    setEditingAsset(null);
  };

  const saveEditing = (index) => {
    setFormData(prev => ({
      ...prev,
      assets: prev.assets.map((a, i) => i === index ? { ...editingAsset, cost: parseFloat(editingAsset.cost) || 0 } : a)
    }));
    setEditingIndex(null);
    setEditingAsset(null);
  };

  const toggleExpand = (index) => {
    setExpandedAssets(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  // Calculate total asset value
  const totalAssetValue = assets.reduce((sum, asset) => {
    return sum + (asset.status === 'Assigned' ? (parseFloat(asset.cost) || 0) : 0);
  }, 0);

  const assignedCount = assets.filter(a => a.status === 'Assigned').length;
  const returnedCount = assets.filter(a => a.status === 'Returned').length;
  const hasReturned = assets.some(a => ['Returned', 'Damaged', 'Lost'].includes(a.status));

  const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

  const saveBlob = (data, filename) => {
    const url = window.URL.createObjectURL(new Blob([data]));
    const a = document.createElement('a');
    a.href = url; a.download = filename;
    document.body.appendChild(a); a.click(); a.remove();
    window.URL.revokeObjectURL(url);
  };

  const downloadHandover = async () => {
    if (!employeeId) return;
    setDownloading('handover');
    try {
      const res = await axios.get(`${API}/api/asset-docs/handover/${employeeId}`, { headers: authHeaders(), responseType: 'blob' });
      saveBlob(res.data, `Asset_Handover_${formData.employee_code || 'employee'}.pdf`);
    } catch (e) {
      const msg = e?.response?.data instanceof Blob ? 'Save the employee first, or ensure assets are Assigned.' : (e?.response?.data?.detail || 'Could not generate PDF');
      toast({ title: 'Handover PDF', description: msg, variant: 'destructive' });
    } finally { setDownloading(null); }
  };

  const downloadReceiving = async () => {
    if (!employeeId) return;
    setDownloading('receiving');
    try {
      const items = assets.filter(a => ['Returned', 'Damaged', 'Lost'].includes(a.status))
        .map(a => ({ asset_id: String(a.id), returned: true, condition_returned: a.condition_returned || a.status, notes: a.notes || '' }));
      const res = await axios.post(`${API}/api/asset-docs/receiving/${employeeId}`, { items, persist: false }, { headers: authHeaders(), responseType: 'blob' });
      saveBlob(res.data, `Asset_Return_${formData.employee_code || 'employee'}.pdf`);
    } catch (e) {
      toast({ title: 'Receiving PDF', description: 'Mark at least one asset as Returned/Damaged/Lost first.', variant: 'destructive' });
    } finally { setDownloading(null); }
  };

  const openDiscSettings = async () => {
    setShowDiscSettings(true);
    if (!disc) {
      try {
        const res = await axios.get(`${API}/api/asset-docs/disclaimers`, { headers: authHeaders() });
        setDisc(res.data);
      } catch { setDisc({ handover_disclaimer: '', return_disclaimer: '' }); }
    }
  };

  const saveDisc = async () => {
    setSavingDisc(true);
    try {
      const res = await axios.put(`${API}/api/asset-docs/disclaimers`, {
        handover_disclaimer: disc.handover_disclaimer, return_disclaimer: disc.return_disclaimer,
      }, { headers: authHeaders() });
      setDisc(res.data);
      toast({ title: 'Disclaimers saved', description: 'Used on all future handover & return PDFs.' });
      setShowDiscSettings(false);
    } catch { toast({ title: 'Could not save', variant: 'destructive' }); }
    finally { setSavingDisc(false); }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Assigned': return 'bg-green-500';
      case 'Returned': return 'bg-blue-500';
      case 'Lost': return 'bg-red-500';
      case 'Damaged': return 'bg-orange-500';
      default: return 'bg-gray-500';
    }
  };

  const getConditionColor = (condition) => {
    switch (condition) {
      case 'New': return 'text-green-600';
      case 'Excellent': return 'text-emerald-600';
      case 'Good': return 'text-blue-600';
      case 'Fair': return 'text-amber-600';
      case 'Poor': return 'text-red-600';
      default: return 'text-gray-600';
    }
  };

  return (
    <div className="border rounded-lg p-4 bg-gradient-to-br from-emerald-50 to-teal-50">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold flex items-center gap-2 text-emerald-700">
          <Package className="h-5 w-5" />
          Company Assets
          {assets.length > 0 && (
            <Badge variant="secondary">{assets.length}</Badge>
          )}
        </h3>
        <div className="flex items-center gap-2 flex-wrap justify-end">
          {employeeId && assignedCount > 0 && (
            <Button type="button" size="sm" variant="outline" onClick={downloadHandover} disabled={downloading === 'handover'}
              className="border-emerald-300 text-emerald-700 hover:bg-emerald-100" data-testid="handover-pdf-btn">
              {downloading === 'handover' ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <FileDown className="h-4 w-4 mr-1" />} Handover PDF
            </Button>
          )}
          {employeeId && hasReturned && (
            <Button type="button" size="sm" variant="outline" onClick={downloadReceiving} disabled={downloading === 'receiving'}
              className="border-blue-300 text-blue-700 hover:bg-blue-100" data-testid="receiving-pdf-btn">
              {downloading === 'receiving' ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <FileDown className="h-4 w-4 mr-1" />} Receiving PDF
            </Button>
          )}
          <Button type="button" size="sm" variant="ghost" onClick={openDiscSettings} title="Edit disclaimer wording"
            className="text-gray-500 hover:text-gray-700" data-testid="asset-settings-btn">
            <Settings2 className="h-4 w-4" />
          </Button>
          <Button
            type="button"
            size="sm"
            onClick={() => setShowAddForm(!showAddForm)}
            className="bg-emerald-600 hover:bg-emerald-700"
            data-testid="add-asset-btn"
          >
            <Plus className="h-4 w-4 mr-1" />
            Add Asset
          </Button>
        </div>
      </div>

      {/* Asset Summary */}
      {assets.length > 0 && (
        <div className="bg-white rounded-lg p-3 mb-4 border border-emerald-200">
          <div className="grid grid-cols-3 gap-4 text-center">
            <div>
              <p className="text-2xl font-bold text-emerald-600">{assignedCount}</p>
              <p className="text-xs text-gray-500">Assigned</p>
            </div>
            <div>
              <p className="text-2xl font-bold text-blue-600">{returnedCount}</p>
              <p className="text-xs text-gray-500">Returned</p>
            </div>
            <div>
              <p className="text-lg font-bold text-gray-700"><Money value={totalAssetValue} /></p>
              <p className="text-xs text-gray-500">Total Value (Assigned)</p>
            </div>
          </div>
        </div>
      )}

      {/* Add Asset Form */}
      {showAddForm && (
        <div className="bg-white rounded-lg p-4 mb-4 border border-emerald-200">
          <h4 className="font-medium text-emerald-700 mb-3">Add New Asset</h4>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <Label>Product Name *</Label>
              <Input
                value={newAsset.product_name}
                onChange={(e) => setNewAsset(prev => ({ ...prev, product_name: e.target.value }))}
                placeholder="e.g., Laptop, Phone, Uniform"
                data-testid="asset-name-input"
              />
            </div>
            <div>
              <Label>Serial Number</Label>
              <Input
                value={newAsset.serial_number}
                onChange={(e) => setNewAsset(prev => ({ ...prev, serial_number: e.target.value }))}
                placeholder="Product serial number (if any)"
                data-testid="asset-serial-input"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <div>
              <Label>Condition When Given</Label>
              <Select
                value={newAsset.condition_given}
                onValueChange={(value) => setNewAsset(prev => ({ ...prev, condition_given: value }))}
              >
                <SelectTrigger data-testid="asset-condition-given-select">
                  <SelectValue placeholder="Select condition" />
                </SelectTrigger>
                <SelectContent>
                  {CONDITION_OPTIONS.map((cond) => (
                    <SelectItem key={cond} value={cond}>{cond}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Cost (AED)</Label>
              <Input
                type="number"
                value={newAsset.cost}
                onChange={(e) => setNewAsset(prev => ({ ...prev, cost: e.target.value }))}
                placeholder="e.g., 5000"
                data-testid="asset-cost-input"
              />
            </div>
            <div>
              <Label>Date Given</Label>
              <Input
                type="date"
                value={newAsset.date_given}
                onChange={(e) => setNewAsset(prev => ({ ...prev, date_given: e.target.value }))}
                data-testid="asset-date-given-input"
              />
            </div>
          </div>

          <div className="mb-4">
            <Label>Notes</Label>
            <Textarea
              value={newAsset.notes}
              onChange={(e) => setNewAsset(prev => ({ ...prev, notes: e.target.value }))}
              placeholder="Any additional notes about this asset..."
              rows={2}
              data-testid="asset-notes-input"
            />
          </div>

          <div className="flex gap-2 justify-end">
            <Button type="button" variant="outline" onClick={() => setShowAddForm(false)}>
              Cancel
            </Button>
            <Button 
              type="button" 
              onClick={addAsset}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="save-asset-btn"
            >
              Add Asset
            </Button>
          </div>
        </div>
      )}

      {/* Assets List */}
      {assets.length === 0 ? (
        <p className="text-sm text-gray-500 italic text-center py-4">No assets assigned</p>
      ) : (
        <div className="space-y-2">
          {assets.map((asset, index) => (
            <div key={asset.id || index} className="bg-white rounded-lg border border-emerald-200 overflow-hidden">
              {/* Header - Always visible */}
              <div 
                className="flex items-center justify-between p-3 cursor-pointer hover:bg-gray-50"
                onClick={() => toggleExpand(index)}
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
                    <Package className="h-5 w-5 text-emerald-600" />
                  </div>
                  <div>
                    <p className="font-medium">{asset.product_name}</p>
                    <div className="flex items-center gap-2 flex-wrap">
                      <Badge className={`${getStatusColor(asset.status)} text-xs`}>
                        {asset.status}
                      </Badge>
                      {asset.serial_number && (
                        <span className="text-xs text-gray-500">S/N: {asset.serial_number}</span>
                      )}
                      {asset.cost > 0 && (
                        <span className="text-xs text-gray-600"><Money value={asset.cost} /></span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      startEditing(index, asset);
                      setExpandedAssets(prev => ({ ...prev, [index]: true }));
                    }}
                    data-testid={`edit-asset-${index}-btn`}
                  >
                    <Edit2 className="h-4 w-4 text-blue-500" />
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      removeAsset(index);
                    }}
                    data-testid={`remove-asset-${index}-btn`}
                  >
                    <Trash2 className="h-4 w-4 text-red-500" />
                  </Button>
                  {expandedAssets[index] ? (
                    <ChevronUp className="h-5 w-5 text-gray-400" />
                  ) : (
                    <ChevronDown className="h-5 w-5 text-gray-400" />
                  )}
                </div>
              </div>

              {/* Expanded Details / Edit Form */}
              {expandedAssets[index] && (
                <div className="border-t p-3 bg-gray-50">
                  {editingIndex === index ? (
                    // Edit Mode
                    <div className="space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <Label>Product Name</Label>
                          <Input
                            value={editingAsset.product_name}
                            onChange={(e) => setEditingAsset(prev => ({ ...prev, product_name: e.target.value }))}
                          />
                        </div>
                        <div>
                          <Label>Serial Number</Label>
                          <Input
                            value={editingAsset.serial_number || ''}
                            onChange={(e) => setEditingAsset(prev => ({ ...prev, serial_number: e.target.value }))}
                          />
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          <Label>Status</Label>
                          <Select
                            value={editingAsset.status}
                            onValueChange={(value) => setEditingAsset(prev => ({ ...prev, status: value }))}
                          >
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {STATUS_OPTIONS.map((s) => (
                                <SelectItem key={s} value={s}>{s}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label>Condition Given</Label>
                          <Select
                            value={editingAsset.condition_given}
                            onValueChange={(value) => setEditingAsset(prev => ({ ...prev, condition_given: value }))}
                          >
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {CONDITION_OPTIONS.map((c) => (
                                <SelectItem key={c} value={c}>{c}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label>Condition Returned</Label>
                          <Select
                            value={editingAsset.condition_returned || 'not_returned'}
                            onValueChange={(value) => setEditingAsset(prev => ({ 
                              ...prev, 
                              condition_returned: value === 'not_returned' ? null : value 
                            }))}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Select when returned" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="not_returned">Not returned yet</SelectItem>
                              {CONDITION_OPTIONS.map((c) => (
                                <SelectItem key={c} value={c}>{c}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          <Label>Cost (AED)</Label>
                          <Input
                            type="number"
                            value={editingAsset.cost || ''}
                            onChange={(e) => setEditingAsset(prev => ({ ...prev, cost: e.target.value }))}
                          />
                        </div>
                        <div>
                          <Label>Date Given</Label>
                          <Input
                            type="date"
                            value={editingAsset.date_given || ''}
                            onChange={(e) => setEditingAsset(prev => ({ ...prev, date_given: e.target.value }))}
                          />
                        </div>
                        <div>
                          <Label>Date Returned</Label>
                          <Input
                            type="date"
                            value={editingAsset.date_returned || ''}
                            onChange={(e) => setEditingAsset(prev => ({ ...prev, date_returned: e.target.value }))}
                          />
                        </div>
                      </div>

                      <div>
                        <Label>Notes</Label>
                        <Textarea
                          value={editingAsset.notes || ''}
                          onChange={(e) => setEditingAsset(prev => ({ ...prev, notes: e.target.value }))}
                          rows={2}
                        />
                      </div>

                      <div className="flex gap-2 justify-end">
                        <Button type="button" variant="outline" size="sm" onClick={cancelEditing}>
                          <X className="h-4 w-4 mr-1" /> Cancel
                        </Button>
                        <Button 
                          type="button" 
                          size="sm"
                          onClick={() => saveEditing(index)}
                          className="bg-emerald-600 hover:bg-emerald-700"
                        >
                          <Check className="h-4 w-4 mr-1" /> Save
                        </Button>
                      </div>
                    </div>
                  ) : (
                    // View Mode
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                      <div>
                        <span className="text-gray-500 block">Condition Given</span>
                        <span className={`font-medium ${getConditionColor(asset.condition_given)}`}>
                          {asset.condition_given}
                        </span>
                      </div>
                      <div>
                        <span className="text-gray-500 block">Condition Returned</span>
                        <span className={`font-medium ${asset.condition_returned ? getConditionColor(asset.condition_returned) : 'text-gray-400'}`}>
                          {asset.condition_returned || 'N/A'}
                        </span>
                      </div>
                      <div>
                        <span className="text-gray-500 block">Date Given</span>
                        <span className="font-medium">
                          {asset.date_given ? new Date(asset.date_given).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' }) : 'N/A'}
                        </span>
                      </div>
                      <div>
                        <span className="text-gray-500 block">Date Returned</span>
                        <span className="font-medium">
                          {asset.date_returned ? new Date(asset.date_returned).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' }) : 'N/A'}
                        </span>
                      </div>
                      {asset.notes && (
                        <div className="col-span-full">
                          <span className="text-gray-500 block">Notes</span>
                          <span className="text-gray-700">{asset.notes}</span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Auto disclaimer footer — appears whenever assets are assigned */}
      {assets.length > 0 && (
        <div className="mt-4 rounded-lg border border-amber-300 bg-amber-50 p-3" data-testid="assets-disclaimer">
          <p className="text-[11px] font-semibold uppercase tracking-wide text-amber-700 mb-1">
            Asset Custody &amp; Liability Declaration
          </p>
          <p className="text-xs leading-relaxed text-amber-900">
            <span className="font-semibold">{(formData.name || '').trim() || 'The employee'}</span>{' '}
            hereby acknowledges receipt of the company asset(s) listed above and undertakes to keep
            them safe, secure and in good working condition throughout the period of custody.
            Reasonable and normal wear and tear arising from ordinary, intended use is acceptable.
            However, <span className="font-semibold">{(formData.name || '').trim() || 'the employee'}</span>{' '}
            shall bear sole responsibility for any loss, theft, misuse, negligence, or damage to the
            asset(s), and the Company reserves the right to recover and/or deduct the current
            replacement or market value of the affected asset(s) from the employee's salary, final
            settlement, or any other sums payable, in accordance with applicable law and Company policy.
          </p>
        </div>
      )}

      {/* Delete Asset Confirmation Dialog */}
      <DeleteConfirmDialog
        open={deleteConfirmOpen}
        onOpenChange={setDeleteConfirmOpen}
        title="Remove Asset"
        description="Are you sure you want to remove this asset from the employee's record?"
        itemName={assetToDelete ? `${assetToDelete.name}${assetToDelete.serial ? ` (${assetToDelete.serial})` : ''}` : ''}
        onConfirm={confirmRemoveAsset}
      />

      {/* Editable disclaimers */}
      <Dialog open={showDiscSettings} onOpenChange={setShowDiscSettings}>
        <DialogContent className="max-w-lg" data-testid="asset-disclaimer-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Settings2 className="h-5 w-5 text-emerald-600" /> Asset Settings — Disclaimers</DialogTitle>
          </DialogHeader>
          {!disc ? (
            <div className="py-8 text-center text-gray-400"><Loader2 className="h-5 w-5 animate-spin mx-auto" /></div>
          ) : (
            <div className="space-y-4">
              <p className="text-xs text-gray-500">Use <code className="bg-gray-100 px-1 rounded">{'{employee_name}'}</code> and <code className="bg-gray-100 px-1 rounded">{'{company}'}</code> as placeholders — they auto-fill on the PDFs.</p>
              <div>
                <Label className="text-sm font-medium">Handover disclaimer (issuing to employee)</Label>
                <Textarea rows={5} value={disc.handover_disclaimer || ''} onChange={e => setDisc(d => ({ ...d, handover_disclaimer: e.target.value }))} data-testid="handover-disclaimer-input" />
              </div>
              <div>
                <Label className="text-sm font-medium">Return disclaimer (receiving from employee)</Label>
                <Textarea rows={5} value={disc.return_disclaimer || ''} onChange={e => setDisc(d => ({ ...d, return_disclaimer: e.target.value }))} data-testid="return-disclaimer-input" />
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setShowDiscSettings(false)}>Cancel</Button>
                <Button onClick={saveDisc} disabled={savingDisc} className="bg-emerald-600 hover:bg-emerald-700" data-testid="save-disclaimer-btn">
                  {savingDisc ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Check className="h-4 w-4 mr-1" />} Save
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AssetsSection;
